import type React from 'react'
import { X } from 'lucide-react'
import { theme } from '../../theme'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm?: () => void
  confirmLoading?: boolean
  title?: React.ReactNode
  children: React.ReactNode
  footer?: React.ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl'
  width?: number | string
  bodyStyle?: React.CSSProperties
  destroyOnClose?: boolean
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  confirmLoading,
  title,
  children,
  footer,
  size = 'md',
  width,
  bodyStyle,
  destroyOnClose,
}) => {
  if (!isOpen) return null

  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1100,
    backdropFilter: 'blur(4px)',
  }

  const sizeMap = {
    sm: '400px',
    md: '600px',
    lg: '800px',
    xl: '1000px',
  }

  const modalStyle: React.CSSProperties = {
    backgroundColor: theme.colors.background,
    borderRadius: theme.borderRadius.lg,
    boxShadow: theme.shadows.large,
    width: width ? (typeof width === 'number' ? `${width}px` : width) : '90%',
    maxWidth: width ? undefined : sizeMap[size],
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  }

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing.lg,
    borderBottom: `1px solid ${theme.colors.border}`,
  }

  const titleStyle: React.CSSProperties = {
    ...theme.typography.h2,
    color: theme.colors.text.primary,
  }

  const closeButtonStyle: React.CSSProperties = {
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    color: theme.colors.text.secondary,
    borderRadius: theme.borderRadius.sm,
    transition: `all ${theme.animations.duration.fast}ms`,
  }

  const contentStyle: React.CSSProperties = {
    flex: 1,
    padding: theme.spacing.lg,
    overflowY: 'auto',
    ...bodyStyle,
  }

  const footerStyle: React.CSSProperties = {
    padding: theme.spacing.lg,
    borderTop: `1px solid ${theme.colors.border}`,
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing.sm,
  }

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          {title && <div style={titleStyle}>{title}</div>}
          <button style={closeButtonStyle} onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        <div style={contentStyle}>
          {children}
        </div>
        {footer && (
          <div style={footerStyle}>
            {footer}
          </div>
        )}
      </div>
    </div>
  )
}
